
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";
import { 
  IMAGE_PATHS, 
  FILE_NAMES,
  validateImageFile,
  validateImageUrl,
  uploadStorageFile,
  uploadFromUrl,
  StorageError 
} from '@/utils/image-storage';
import { optimizeImage } from "./imageOptimization";

const COMPONENT = 'LogoUploadHandler';

interface LogoUploadProps {
  companyId: string;
  onSuccess: (publicUrl: string) => Promise<void>;
  setIsUploading: (isUploading: boolean) => void;
}

export async function handleFileUpload(file: File, props: LogoUploadProps): Promise<boolean> {
  const { companyId, onSuccess, setIsUploading } = props;

  if (!file) {
    toast.error("No file selected");
    return false;
  }

  try {
    logger.info(COMPONENT, 'Starting file upload', {
      companyId,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size
    });

    setIsUploading(true);
    validateImageFile(file);

    const optimizedFile = await optimizeImage(file);
    
    const publicUrl = await uploadStorageFile(
      IMAGE_PATHS.COMPANY(companyId),
      FILE_NAMES.COMPANY_LOGO,
      optimizedFile,
      { upsert: true }
    );
    
    await onSuccess(publicUrl);

    logger.info(COMPONENT, 'File upload successful', {
      companyId,
      publicUrl
    });

    return true;
  } catch (error: any) {
    logger.error(COMPONENT, 'File upload failed', {
      error: error.message,
      details: error instanceof StorageError ? error.details : error
    });
    
    toast.error(error.message || "Failed to upload logo");
    return false;
  } finally {
    setIsUploading(false);
  }
}

export async function handleUrlUpload(logoUrl: string, props: LogoUploadProps): Promise<boolean> {
  const { companyId, onSuccess, setIsUploading } = props;

  if (!logoUrl.trim()) {
    toast.error("Please enter a valid logo URL");
    return false;
  }

  try {
    logger.info(COMPONENT, 'Starting URL upload', {
      companyId,
      logoUrl
    });

    setIsUploading(true);
    validateImageUrl(logoUrl);
    
    const publicUrl = await uploadFromUrl(
      IMAGE_PATHS.COMPANY(companyId),
      FILE_NAMES.COMPANY_LOGO,
      logoUrl,
      { upsert: true }
    );

    await onSuccess(publicUrl);

    logger.info(COMPONENT, 'URL upload successful', {
      companyId,
      publicUrl
    });

    return true;
  } catch (error: any) {
    logger.error(COMPONENT, 'URL upload failed', {
      error: error.message,
      details: error instanceof StorageError ? error.details : error
    });
    
    toast.error(error.message || "Failed to update logo URL");
    return false;
  } finally {
    setIsUploading(false);
  }
}
